import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ProviderServicesModal from './ProviderServicesModal';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import ProviderResultCard from './providers/ProviderResultCard';
import { Reveal } from './site/motion';
import {
  Search,
  SearchX,
  MapPin,
  Star,
  X,
  HeartPulse,
  Sparkles,
  Scissors,
  SprayCan,
  Waves,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HealthcareProvidersPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { user, sessionToken } = useUnifiedAuth();
  const isAdmin = user?.user_type === 'admin';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState(''); // For immediate UI updates
  const [searchTerm, setSearchTerm] = useState(''); // For actual search queries
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Flag to prevent race condition
  const [selectedProviderForServices, setSelectedProviderForServices] = useState(null);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [nearMe, setNearMe] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [geoError, setGeoError] = useState('');
  const [sortByOption, setSortByOption] = useState('default');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sponsoredProviders, setSponsoredProviders] = useState([]);

  const moveProvider = async (index, direction) => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= providers.length) return;

    const newProviders = [...providers];
    [newProviders[index], newProviders[targetIndex]] = [newProviders[targetIndex], newProviders[index]];
    
    // Update display_order for both providers
    try {
      const updates = [
        fetch(`${BACKEND_URL}/api/admin/providers/${newProviders[index].id}/order`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ display_order: index })
        }),
        fetch(`${BACKEND_URL}/api/admin/providers/${newProviders[targetIndex].id}/order`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ display_order: targetIndex })
        })
      ];

      const results = await Promise.all(updates);
      
      if (results.every(r => r.ok)) {
        setProviders(newProviders);
      } else {
        alert('❌ Failed to update provider order');
      }
    } catch (error) {
      console.error('Error updating provider order:', error);
      alert('❌ Failed to update provider order');
    }
  };

  const deleteProvider = async (provider) => {
    const providerName = provider.name || provider.facility_name || 'this provider';
    const providerType = provider.type || provider.provider_type || (provider.clinic_name ? 'doctor' : 'provider');
    
    if (!window.confirm(`⚠️ Are you sure you want to PERMANENTLY DELETE "${providerName}"?\n\nThis will:\n• Remove the ${providerType} from the platform\n• Delete all their services\n• Cancel all pending appointments\n• Remove all staff members\n\nThis action CANNOT be undone!`)) {
      return;
    }

    const confirmText = prompt(`To confirm deletion, type: DELETE ${providerName}`);
    if (confirmText !== `DELETE ${providerName}`) {
      alert('❌ Deletion cancelled - confirmation text did not match');
      return;
    }

    try {
      // Delete from the appropriate collection (doctor or provider)
      let deleteEndpoint;
      if (providerType === 'doctor') {
        deleteEndpoint = `${BACKEND_URL}/api/admin/doctors/${provider.id}`;
      } else {
        deleteEndpoint = `${BACKEND_URL}/api/admin/providers/${provider.id}`;
      }

      const response = await fetch(deleteEndpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        alert(`✅ ${providerType.charAt(0).toUpperCase() + providerType.slice(1)} "${providerName}" deleted successfully`);
        
        // Remove from local state
        setProviders(providers.filter(p => p.id !== provider.id));
        setFilteredProviders(filteredProviders.filter(p => p.id !== provider.id));
        
        // Refresh from server
        setTimeout(() => fetchProviders(), 500);
      } else {
        const errorData = await response.json();
        alert(`❌ Failed to delete ${providerType}: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting provider:', error);
      alert(`❌ Error deleting ${providerType}`);
    }
  };

  const handleToggleListing = async (provider) => {
    const isCurrentlyListed = provider.is_listed !== false;
    const action = isCurrentlyListed ? 'remove from website' : 'add to website';
    const providerType = provider.type || provider.provider_type || (provider.clinic_name ? 'doctor' : 'provider');
    
    if (!window.confirm(`Are you sure you want to ${action} this ${providerType}?`)) {
      return;
    }

    try {
      const userId = provider.user_id || provider.id;
      const providerId = provider.id;
      
      console.log('Toggling listing for:', provider.name);
      console.log('Type:', providerType);
      console.log('Provider ID:', providerId);
      console.log('User ID:', userId);
      console.log('Current listed status:', provider.is_listed);
      console.log('New listed status:', !isCurrentlyListed);
      
      let providerUpdateSuccess = false;
      let userUpdateSuccess = false;
      
      // 1. Update provider/doctor collection (PRIMARY - most important)
      try {
        const providerEndpoint = providerType === 'doctor' 
          ? `${BACKEND_URL}/api/doctors/${providerId}`
          : `${BACKEND_URL}/api/healthcare-providers/${providerId}`;
        
        const providerResponse = await fetch(providerEndpoint, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_listed: !isCurrentlyListed })
        });

        if (providerResponse.ok) {
          providerUpdateSuccess = true;
          console.log('✅ Provider/Doctor collection updated');
        } else {
          const errorData = await providerResponse.json();
          console.error('❌ Provider update failed:', errorData);
        }
      } catch (error) {
        console.error('❌ Provider update error:', error);
      }

      // 2. Update user collection (SECONDARY - nice to have but not required)
      try {
        const userResponse = await fetch(`${BACKEND_URL}/api/admin/edit-user/${userId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_listed: !isCurrentlyListed })
        });

        if (userResponse.ok) {
          userUpdateSuccess = true;
          console.log('✅ User collection updated');
        } else {
          const errorData = await userResponse.json();
          console.log('⚠️ User update failed (may not exist):', errorData.detail);
        }
      } catch (error) {
        console.log('⚠️ User update error (may not exist):', error.message);
      }

      // Success if provider/doctor was updated (user update is optional)
      if (providerUpdateSuccess) {
        const statusText = !isCurrentlyListed ? 'listed' : 'unlisted';
        const warningText = !userUpdateSuccess ? ' (Note: User account not found, but provider record updated)' : '';
        alert(`✅ Successfully ${statusText} ${providerType}${warningText}`);
        
        // Update local state immediately
        const updatedProvidersList = providers.map(p => 
          p.id === provider.id 
            ? { ...p, is_listed: !isCurrentlyListed }
            : p
        );
        
        setProviders(updatedProvidersList);
        setFilteredProviders(filteredProviders.map(p => 
          p.id === provider.id 
            ? { ...p, is_listed: !isCurrentlyListed }
            : p
        ));
        
        // Refresh from server
        setTimeout(() => fetchProviders(), 500);
      } else {
        alert(`❌ Failed to update ${providerType}. Please try again or check the console for errors.`);
      }
    } catch (error) {
      console.error('Error toggling listing:', error);
      alert('❌ Error updating listing status');
    }
  };

  const handleMessageClick = async (provider) => {
    if (!user || !sessionToken) {
      // Redirect to login if not authenticated
      navigate('/');
      return;
    }

    try {
      // Create or get existing conversation with provider
      const response = await axios.post(
        `${API}/conversations`,
        null,
        {
          params: {
            participant_id: provider.user_id || provider.id,
            initial_message: `Hi ${provider.name}, I would like to inquire about your services.`
          },
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        // Navigate to messages page with the conversation
        navigate('/messages');
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      if (error.response?.status === 403) {
        alert('You need to book an appointment first to message this healthcare provider.');
      } else {
        alert('Unable to start conversation. Please try again later.');
      }
    }
  };

  // Google Maps integration
  const openGoogleMaps = (provider) => {
    const location = provider.area || provider.location?.area;
    const governorate = provider.governorate || provider.location?.governorate;
    const address = provider.address;
    
    let searchQuery = '';
    
    if (address) {
      // Use full address if available
      searchQuery = `${address}, Kuwait`;
    } else if (location && governorate) {
      // Use area and governorate
      searchQuery = `${location}, ${governorate}, Kuwait`;
    } else if (location) {
      // Use just area
      searchQuery = `${location}, Kuwait`;
    } else {
      // Fallback to provider name
      searchQuery = `${provider.name}, Kuwait`;
    }
    
    // Open Google Maps with the search query
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`;
    window.open(googleMapsUrl, '_blank');
  };

  // Hospitals & Pharmacies are hidden from discovery in this phase
  const categories = [
    'Healthcare Clinic', 
    'Beauty Clinic',
    'Dental Clinic',
    'Spa',
    'Salon',
    'Barbershop'
  ];

  // Medical Specialties
  const specialties = [
    'Allergy and Immunology',
    'Anesthesiology', 
    'Cardiology',
    'Dermatology',
    'Emergency Medicine',
    'Endocrinology',
    'Family Medicine',
    'Gastroenterology',
    'General Surgery',
    'Geriatrics',
    'Gynecology',
    'Hematology',
    'Infectious Disease',
    'Internal Medicine',
    'Nephrology',
    'Neurology',
    'Obstetrics and Gynecology',
    'Oncology',
    'Ophthalmology',
    'Orthodontics',
    'Orthopedic Surgery',
    'Otolaryngology (ENT)',
    'Pathology',
    'Pediatrics',
    'Physical Medicine',
    'Plastic Surgery',
    'Psychiatry',
    'Pulmonology',
    'Radiology',
    'Rehabilitation',
    'Rheumatology',
    'Sleep Medicine',
    'Sports Medicine',
    'Thoracic Surgery',
    'Urology',
    'Vascular Surgery'
  ];

  // Initialize URL parameters on component mount
  useEffect(() => {
    const category = searchParams.get('category');
    const gender = searchParams.get('gender_preference');
    const search = searchParams.get('search');
    
    // Create override filters from URL parameters
    const urlFilters = {};
    if (category && categories.includes(category)) {
      setCategoryFilter(category);
      urlFilters.categoryFilter = category;
    }
    if (gender) {
      setGenderFilter(gender);
      urlFilters.genderFilter = gender;
    }
    if (search) {
      setInputValue(search);
      setSearchTerm(search);
      urlFilters.searchTerm = search;
    }
    
    // Fetch providers immediately with URL parameters (don't wait for state updates)
    fetchProviders(urlFilters);
    
    // Mark initial load as complete to prevent race condition
    setIsInitialLoad(false);
  }, [searchParams]);

  // Debounce search input - only update searchTerm after user stops typing
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 800);

    return () => clearTimeout(delayedSearch);
  }, [inputValue]);

  // ONLY fetch providers when filters actually change (after initial load)
  useEffect(() => {
    if (!isInitialLoad) {
      fetchProviders();
    }
  }, [searchTerm, categoryFilter, specialtyFilter, genderFilter, isInitialLoad, userCoords, sortByOption]);

  // Fetch sponsored providers
  useEffect(() => {
    const fetchSponsored = async () => {
      try {
        const res = await axios.get(`${API}/sponsored-providers`);
        setSponsoredProviders(res.data.providers || []);
      } catch (err) {
        console.error('Error fetching sponsored:', err);
      }
    };
    fetchSponsored();
  }, []);

  const handleNearMeToggle = () => {
    if (nearMe) {
      setNearMe(false);
      setUserCoords(null);
      setGeoError('');
      return;
    }
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser');
      return;
    }
    setGeoError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setNearMe(true);
      },
      () => {
        setGeoError('Unable to get your location. Please enable location access.');
      }
    );
  };

  const fetchProviders = async (overrideFilters = {}) => {
    try {
      setLoading(true);
      
      // Use current state or override filters (for URL parameter handling)
      const currentFilters = {
        searchTerm: overrideFilters.searchTerm || searchTerm,
        categoryFilter: overrideFilters.categoryFilter || categoryFilter,
        specialtyFilter: overrideFilters.specialtyFilter || specialtyFilter,
        genderFilter: overrideFilters.genderFilter || genderFilter
      };
      
      // Build query parameters for enhanced search
      const params = new URLSearchParams();
      if (currentFilters.searchTerm) params.append('search', currentFilters.searchTerm);
      if (currentFilters.categoryFilter !== 'all') params.append('category', currentFilters.categoryFilter);
      if (currentFilters.specialtyFilter !== 'all') params.append('specialty', currentFilters.specialtyFilter);
      if (currentFilters.genderFilter !== 'all') params.append('gender_preference', currentFilters.genderFilter);
      if (userCoords) {
        params.append('lat', userCoords.lat);
        params.append('lng', userCoords.lng);
      }
      if (sortByOption && sortByOption !== 'default') {
        params.append('sort_by', sortByOption);
      }
      
      const queryString = params.toString();
      const url = queryString ? `${API}/healthcare-providers?${queryString}` : `${API}/healthcare-providers`;
      
      const response = await axios.get(url);
      const providersData = response.data.providers || [];
      
      setProviders(providersData);
      setFilteredProviders(providersData);
    } catch (error) {
      console.error('Error fetching providers:', error);
      setProviders([]);
      setFilteredProviders([]);
    } finally {
      setLoading(false);
    }
  };

  // Map existing database categories to new categories
  const mapToNewCategory = (category, subcategory, type) => {
    // Handle new direct categories first
    if (category === 'Spa') return 'Spa';
    if (category === 'Salon') return 'Salon';
    if (category === 'Barbershop') return 'Barbershop';
    
    // Handle existing mapping logic
    if (subcategory === 'beauty' || (category === 'private' && type === 'clinic' && subcategory === 'beauty')) {
      return 'Beauty Clinic';
    }
    if (type === 'hospital') {
      return 'Hospital';  
    }
    if (type === 'clinic' && subcategory !== 'beauty') {
      return 'Healthcare Clinic';
    }
    // Default fallback
    return 'Healthcare Clinic';
  };

  const handleSearchChange = (e) => {
    setInputValue(e.target.value);
  };

  const clearFilters = () => {
    setInputValue('');
    setSearchTerm('');
    setCategoryFilter('all');
    setSpecialtyFilter('all');
    setGenderFilter('all');
    const params = new URLSearchParams(searchParams);
    params.delete('category');
    setSearchParams(params, { replace: true });
  };

  // Keep the URL in step with the chips so a reload (or the nav highlight) stays correct
  const selectCategory = (category) => {
    setCategoryFilter(category);
    const params = new URLSearchParams(searchParams);
    if (category === 'all') params.delete('category');
    else params.set('category', category);
    setSearchParams(params, { replace: true });
  };

  const handleMessageProvider = async (provider) => {
    if (!user || !sessionToken) {
      // Redirect to login if not authenticated
      navigate('/');
      return;
    }

    try {
      // Create or get existing conversation with provider
      const response = await axios.post(
        `${API}/conversations`,
        null,
        {
          params: {
            participant_id: provider.user_id || provider.id,
            initial_message: `Hi ${provider.name}, I would like to inquire about your services.`
          },
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        // Navigate to messages page with the conversation
        navigate('/messages');
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Unable to start conversation. Please try again later.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="h-10 w-64 rounded-2xl bg-gray-200 dark:bg-white/10 animate-pulse" />
          <div className="mt-4 h-5 w-80 rounded-xl bg-gray-200/70 dark:bg-white/5 animate-pulse" />
          <div className="mt-10 flex gap-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 w-28 rounded-full bg-gray-200/80 dark:bg-white/10 animate-pulse" />
            ))}
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="rounded-3xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900/60 overflow-hidden"
              >
                <div className="h-44 bg-gray-200 dark:bg-white/10 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-white/10 animate-pulse" />
                  <div className="h-3 w-full rounded bg-gray-200/70 dark:bg-white/5 animate-pulse" />
                  <div className="h-3 w-2/3 rounded bg-gray-200/70 dark:bg-white/5 animate-pulse" />
                  <div className="h-11 w-full rounded-full bg-gray-200/80 dark:bg-white/10 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const CATEGORY_ICONS = {
    'Healthcare Clinic': HeartPulse,
    'Beauty Clinic': Sparkles,
    'Dental Clinic': ShieldCheck,
    Spa: Waves,
    Salon: Scissors,
    Barbershop: SprayCan,
  };

  const CATEGORY_LABELS = {
    'Healthcare Clinic': t('categories.healthcareClinic', 'Healthcare Clinic'),
    'Beauty Clinic': t('categories.beautyClinic', 'Beauty Clinic'),
    'Dental Clinic': t('categories.dentalClinic', 'Dental Clinic'),
    Spa: t('categories.spa', 'Spa'),
    Salon: t('categories.salon', 'Salon (Ladies)'),
    Barbershop: t('categories.barbershop', 'Barbershop'),
  };

  const CATEGORY_TITLES = {
    'Healthcare Clinic': t('siteNav.healthcare', 'Healthcare Clinics'),
    'Beauty Clinic': t('siteNav.beauty', 'Beauty Clinics'),
    'Dental Clinic': t('siteNav.dental', 'Dental Clinics'),
    Spa: t('siteNav.spas', 'Spas'),
    Salon: t('siteNav.salons', 'Ladies Salons'),
    Barbershop: t('siteNav.barbershops', 'Barbershops'),
  };

  const CATEGORY_SUBTITLES = {
    'Beauty Clinic': t('home.categories.beautyClinicDesc', 'Professional skincare, aesthetics, and beauty treatments'),
    Spa: t('home.categories.spaDesc', 'Relaxation, massage, and wellness treatments'),
    Salon: t('home.categories.salonDesc', 'Hair, nails and beauty for women'),
    Barbershop: t('home.categories.barbershopDesc', 'Haircuts, beards and grooming for men'),
  };

  const genderOptions = [
    { value: 'all', label: t('providersPage.allGenders', 'Everyone') },
    { value: 'men', label: t('providersPage.menOnly', 'Men') },
    { value: 'women', label: t('providersPage.womenOnly', 'Women') },
    { value: 'mixed', label: t('providersPage.mixed', 'Mixed') },
  ];

  const sortOptions = [
    { value: 'default', label: t('providersPage.default') },
    { value: 'rating', label: t('providersPage.topRated') },
    { value: 'name', label: t('providersPage.nameAZ') },
  ];

  const chipBase =
    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] font-semibold transition-all duration-300';
  const chipOff =
    'bg-white dark:bg-white/5 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-white/10 hover:border-blue-300 dark:hover:border-blue-400/40';
  const chipOn =
    'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border border-transparent shadow-md';

  const hasActiveFilters =
    inputValue || categoryFilter !== 'all' || specialtyFilter !== 'all' || genderFilter !== 'all' || nearMe;

  return (
    <div className="relative min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
      <span className="aurora-blob w-[28rem] h-[28rem] -top-40 start-[-8rem] bg-blue-300/40 dark:bg-blue-600/25 animate-aurora-slow" />

      {/* Page header */}
      <div className="relative border-b border-gray-200/80 dark:border-white/10 bg-white/70 dark:bg-gray-950/60 backdrop-blur-sm">
        <div className="absolute inset-0 dot-grid opacity-40 [mask-image:radial-gradient(60%_80%_at_50%_0%,#000,transparent)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400">
            {t('siteNav.discover', 'Discover')}
          </p>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
            {categoryFilter !== 'all'
              ? (CATEGORY_TITLES[categoryFilter] || categoryFilter)
              : t('providersPage.title')}
          </h1>
          <p className="mt-3 max-w-xl text-base text-gray-600 dark:text-gray-400">
            {(categoryFilter !== 'all' && CATEGORY_SUBTITLES[categoryFilter]) ||
              t('providersPage.subtitle', 'Find trusted providers in Kuwait')}
          </p>
        </div>
      </div>

      {/* Sticky filter bar */}
      <div
        data-testid="providers-filter-bar"
        className="sticky-surface sticky top-16 lg:top-[4.75rem] z-30 border-b border-gray-200/80 dark:border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute top-1/2 -translate-y-1/2 start-4 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={inputValue}
                onChange={handleSearchChange}
                placeholder={t('providersPage.searchPlaceholder')}
                data-testid="providers-search-input"
                className="w-full rounded-full border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 ps-11 pe-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition-all"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleNearMeToggle}
                data-testid="near-me-btn"
                className={`${chipBase} ${nearMe ? chipOn : chipOff}`}
              >
                <MapPin className="w-4 h-4" />
                {nearMe ? t('providersPage.nearMeOn') : t('providersPage.nearMe')}
              </button>
              <button
                onClick={() => setShowMobileFilters((v) => !v)}
                data-testid="mobile-filters-toggle"
                aria-expanded={showMobileFilters}
                className={`md:hidden ${chipBase} ${showMobileFilters || hasActiveFilters ? chipOn : chipOff}`}
              >
                <SlidersHorizontal className="w-4 h-4" />
                {t('providersPage.filters', 'Filters')}
              </button>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 dark:bg-blue-500/10 px-3.5 py-2 text-[13px] font-semibold text-blue-700 dark:text-blue-300">
                <span className="numeric-ltr" data-testid="providers-result-count">{filteredProviders.length}</span>
                {t('providersPage.found', 'found')}
              </span>
            </div>
          </div>

          {/* Category chips */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 -mb-1 pretty-scroll" data-testid="category-chip-rail">
            <button
              onClick={() => selectCategory('all')}
              data-testid="category-chip-all"
              className={`${chipBase} ${categoryFilter === 'all' ? chipOn : chipOff}`}
            >
              {t('providersPage.allCategories')}
            </button>
            {categories.map((category) => {
              const Icon = CATEGORY_ICONS[category];
              return (
                <button
                  key={category}
                  onClick={() => {
                    selectCategory(category);
                    if (!['Healthcare Clinic', 'Dental Clinic'].includes(category)) setSpecialtyFilter('all');
                  }}
                  data-testid={`category-chip-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`${chipBase} ${categoryFilter === category ? chipOn : chipOff}`}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {CATEGORY_LABELS[category] || category}
                </button>
              );
            })}
          </div>

          {/* Gender + sort + specialty */}
          <div
            className={`${showMobileFilters ? 'flex' : 'hidden'} md:flex flex-wrap items-center gap-2`}
            data-testid="secondary-filters"
          >
            {genderOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setGenderFilter(opt.value)}
                data-testid={`gender-chip-${opt.value}`}
                className={`${chipBase} ${genderFilter === opt.value ? chipOn : chipOff}`}
              >
                {opt.label}
              </button>
            ))}

            <span className="mx-1 hidden sm:block h-6 w-px bg-gray-200 dark:bg-white/10" />

            <span className="hidden sm:inline text-xs font-semibold text-gray-400">
              {t('providersPage.sort')}
            </span>
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortByOption(opt.value)}
                data-testid={`sort-${opt.value}`}
                className={`${chipBase} ${sortByOption === opt.value ? chipOn : chipOff}`}
              >
                {opt.value === 'rating' && <Star className="w-3.5 h-3.5" />}
                {opt.label}
              </button>
            ))}

            {(categoryFilter === 'all' || ['Healthcare Clinic', 'Dental Clinic'].includes(categoryFilter)) && (
              <select
                value={specialtyFilter}
                onChange={(e) => setSpecialtyFilter(e.target.value)}
                data-testid="specialty-select"
                className="rounded-full border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2 text-[13px] font-semibold text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              >
                <option value="all">{t('providersPage.allSpecialties')}</option>
                {specialties.map((specialty) => (
                  <option key={specialty} value={specialty}>{specialty}</option>
                ))}
              </select>
            )}

            {hasActiveFilters && (
              <button
                onClick={() => { clearFilters(); setNearMe(false); setUserCoords(null); setGeoError(''); }}
                data-testid="clear-filters-btn"
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                {t('providersPage.clearAllFilters')}
              </button>
            )}
            {geoError && <span className="text-xs text-rose-500">{geoError}</span>}
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Sponsored */}
        {sponsoredProviders.length > 0 && (
          <div className="mb-10" data-testid="sponsored-section">
            <h3 className="flex items-center gap-2 font-display text-lg font-bold text-gray-900 dark:text-white mb-4">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              {t('providersPage.featuredProviders')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sponsoredProviders.map((provider, i) => (
                <Reveal key={provider.id} delay={i * 80}>
                  <button
                    onClick={() => navigate(`/healthcare-providers/${provider.id}`)}
                    data-testid="sponsored-provider-card"
                    className="group relative w-full text-start rounded-2xl border border-amber-200/80 dark:border-amber-400/20 bg-amber-50/60 dark:bg-amber-500/[0.06] p-5 lift hover:shadow-xl"
                  >
                    <span
                      className="absolute top-4 end-4 rounded-full bg-amber-400 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-950"
                      data-testid="sponsored-badge"
                    >
                      {provider.sponsored_label || t('providersPage.sponsored')}
                    </span>
                    <div className="flex items-center gap-4">
                      {(provider.logo || provider.profile_image) ? (
                        <img
                          src={provider.logo || provider.profile_image}
                          alt={provider.name}
                          loading="lazy"
                          className="w-14 h-14 rounded-2xl object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <span className="grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white text-lg font-bold">
                          {(provider.name || '?')[0]}
                        </span>
                      )}
                      <div className="min-w-0 pe-16">
                        <h4 className="font-display font-bold text-gray-900 dark:text-white truncate">{provider.name}</h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{CATEGORY_LABELS[provider.category] || provider.category}</p>
                        <div className="mt-1 flex items-center gap-2 text-[11px]">
                          {(provider.average_rating || provider.rating) ? (
                            <span className="inline-flex items-center gap-1 font-semibold text-amber-600 dark:text-amber-400 numeric-ltr">
                              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                              {provider.average_rating || provider.rating}
                            </span>
                          ) : null}
                          {provider.area && <span className="text-gray-500">{provider.area}</span>}
                        </div>
                      </div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProviders.map((provider, index) => (
            <Reveal key={provider.id} delay={Math.min(index, 6) * 70}>
              <ProviderResultCard
                provider={provider}
                category={mapToNewCategory(provider.category, provider.subcategory, provider.type)}
                index={index}
                total={filteredProviders.length}
                isAdmin={isAdmin}
                onOpen={() => navigate(`/healthcare-providers/${provider.id}`)}
                onBook={() => navigate(`/provider/${provider.id}/book`)}
                onServices={() => { setSelectedProviderForServices(provider); setShowServicesModal(true); }}
                onMessage={() => handleMessageClick(provider)}
                onMaps={() => openGoogleMaps(provider)}
                onTag={(tag) => { setInputValue(tag); setSearchTerm(tag); }}
                onMove={moveProvider}
                onToggleListing={handleToggleListing}
                onDelete={deleteProvider}
              />
            </Reveal>
          ))}
        </div>

        {/* Empty */}
        {filteredProviders.length === 0 && (
          <div className="py-20 text-center" data-testid="providers-empty-state">
            <span className="mx-auto grid place-items-center w-16 h-16 rounded-3xl bg-gray-100 dark:bg-white/5">
              <SearchX className="w-7 h-7 text-gray-400" />
            </span>
            <h3 className="font-display mt-6 text-xl font-bold text-gray-900 dark:text-white">
              {t('providersPage.noProviders')}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('providersPage.tryDifferentFilters')}
            </p>
            <button
              onClick={() => { clearFilters(); setNearMe(false); }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 lift"
            >
              {t('providersPage.clearAllFilters')}
            </button>
          </div>
        )}
      </div>

      {/* Services Modal */}
      {selectedProviderForServices && (
        <ProviderServicesModal
          provider={selectedProviderForServices}
          isOpen={showServicesModal}
          onClose={() => {
            setShowServicesModal(false);
            setSelectedProviderForServices(null);
          }}
          onBookService={(service) => {
            window.location.href = `/provider/${selectedProviderForServices.id}/book?service=${service.id}`;
          }}
        />
      )}
    </div>
  );
};

export default HealthcareProvidersPage;
